import downloadjs from "downloadjs";
import * as React from "react";
import * as moment from "moment";
import Datetime from "react-datetime";
import { AppUtils } from "source/framework/appUtils";
import { SaveDataObj } from "source/framework/saveData";
import { IdCode, IIdCode } from "source/models/common/idCode";
import { CheckBox } from "ui/Scripts/source/components/framework/checkbox";
import { ToolButton } from "ui/Scripts/source/components/framework/toolButton";
import { Base } from "ui/Scripts/source/framework/base";
import { CalendarTaskEditItem, ICalendarTaskEditItem } from "ui/Scripts/source/models/calendarTask/calendarTaskEditItem";
import { ICalendarTaskCategoryListItem } from "ui/Scripts/source/models/calendarTaskCategory/calendarTaskCategoryListItem";
import { EndlessList, IEndlessList } from "ui/Scripts/source/models/common/endlessList";
import { FileAttachment, IFileAttachment } from "ui/Scripts/source/models/common/fileAttachment";
import { IdColorTitle } from "ui/Scripts/source/models/common/idColorTitle";
import { SaveAttachmentBase64 } from "ui/Scripts/source/models/common/saveAttachmentBase64";
import { CalendarTaskRepetitionType, CalendarTaskState, ConfirmationDialogResult } from "ui/Scripts/source/models/enums";
import { Translations } from "ui/Scripts/source/models/translations";
import { IModifyUser } from "ui/Scripts/source/models/user/modifyUser";
import { IUnlockResult } from "../../models/common/unlockResult";
import * as attachmentService from "../../models/services/attachmentService";
import * as calendarTaskService from "../../models/services/calendarTaskService";
import * as storeTypes from "../../models/store/storeTypes";
import { IUserState } from "../../models/store/storeTypes";
import { Footer } from "../app/footer";
import { EditPageHeader } from "../app/header";
import { AttachmentList } from "../attachment/attachmentList";
import { CameraPhoto } from "../framework/cameraPhoto";
import { DatePicker } from "../framework/datePicker";
import { FormRow } from "../framework/formRow";
import { SelectIdCodeDialog } from "../framework/selectIdCodeDialog";
import { CalendarTaskRepetitionSettings } from "./calendarTaskRepetitionSettings";
import { CalendarTaskCategoryText, SelectCalendarTaskCategory } from "./selectCalendarTaskCategory";
import { FormGroup } from "../framework/formGroup";
import { CalendarTaskHandlerList } from "./calendarTaskHandlerList";
import { ApiSuccess } from "ui/Scripts/source/models/common/apiSuccess";
import { StoreUtils } from "source/models/store/storeUtils";
import { LockedToOtherUser } from "../locking/lockedToOtherUser";
import { useEffect, useImperativeHandle, useRef, useState } from "react";
import { EditItem } from "source/models/common/editItem";

export interface IRepetitionIntervalYear {
    day: number;
    month: number;
}

interface ICalendarTaskEditViewProps {
    inFullView: boolean;
    isReadOnly: boolean;
    user: IUserState;
    calendarTaskId: string;
    userIsIsSystemAdmin: boolean;
    canSaveCalendarTask: boolean;
    canRemoveCalendarTask: boolean;
    canShareCalendarTasks: boolean;
    calendarTask: ICalendarTaskEditItem;
    categories: ICalendarTaskCategoryListItem[];
    onClose: () => void;
}

export interface ICalendarTaskEditViewRefProps {
    getOrgStateHash: () => string;
    getLockedUser: () => IModifyUser;
    getSaveDataHash: () => Promise<string>;
    save: () => Promise<boolean>;
    close: () => Promise<boolean>;
}

interface ICalendarTaskEditViewState {
    calendarTask: ICalendarTaskEditItem;
    categories: ICalendarTaskCategoryListItem[];
        
    id: string;
    lockedUser: IModifyUser;
    cameraIds: string[];
    showCamera: boolean;
    showingCamera: boolean;
    showSelectReminderDialog: boolean;
    showSelectStateDialog: boolean;
    showSelectCategoryDialog: boolean;
    eventHandlersVisible: boolean;
    repetitionSettingsVisible: boolean;
    parentId: string;
    rowId: string;
    categoryId: string;
    title: string;
    locationText: string;
    orgStartDate: number;
    startDateStr: string;
    startTimeStr: string;
    endDateStr: string;
    endTimeStr: string;
    state: CalendarTaskState;
    allDay: boolean;
    isPrivate: boolean;
    remainder: number;
    description: string;
    repetitionIntervalType: CalendarTaskRepetitionType;
    repetitionInterval: number;
    repetitionIntervalWeek: number[];
    repetitionIntervalMonth: number;
    repetitionIntervalYear: IRepetitionIntervalYear;
    repetitionEndRepeatCount: number;
    repetitionEndDateStr: string;
    handlers: IModifyUser[];
    attachments: IFileAttachment[];
    photoAttachmentIds: string[];
    removedAttachmentIds: string[];
}

const hashIgnoreSaveDataFields = ["id", "rowId"];

const remainderIdCodes = [
    IdCode.createIdCode("-1", Translations.ReminderNone),
    IdCode.createIdCode("0", `0 ${Translations.ReminderMinutesBefore}`),
    IdCode.createIdCode("10", `10 ${Translations.ReminderMinutesBefore}`),
    IdCode.createIdCode("15", `15 ${Translations.ReminderMinutesBefore}`),
    IdCode.createIdCode("30", `30 ${Translations.ReminderMinutesBefore}`),
    IdCode.createIdCode("60", `1 ${Translations.ReminderHourBefore}`),
    IdCode.createIdCode("120", `2 ${Translations.ReminderHoursBefore}`),
    IdCode.createIdCode("180", `3 ${Translations.ReminderHoursBefore}`),
    IdCode.createIdCode("240", `4 ${Translations.ReminderHoursBefore}`),
    IdCode.createIdCode("300", `5 ${Translations.ReminderHoursBefore}`),
    IdCode.createIdCode("600", `10 ${Translations.ReminderHoursBefore}`),
    IdCode.createIdCode("1440", `1 ${Translations.ReminderDayBefore}`),
    IdCode.createIdCode("2880", `2 ${Translations.ReminderDaysBefore}`),
    IdCode.createIdCode("4320", `3 ${Translations.ReminderDaysBefore}`),
    IdCode.createIdCode("5760", `4 ${Translations.ReminderDaysBefore}`),
    IdCode.createIdCode("10080", `1 ${Translations.ReminderWeekBefore}`),
    IdCode.createIdCode("20160", `2 ${Translations.ReminderWeeksBefore}`),
];

const stateIdCodes = [
    IdCode.createIdCode(CalendarTaskState.NotStarted.toString(10), Translations.CalendarTaskStateNotStarted),
    IdCode.createIdCode(CalendarTaskState.Started.toString(10), Translations.CalendarTaskStateStarted),
    IdCode.createIdCode(CalendarTaskState.Finished.toString(10), Translations.CalendarTaskStateFinished),
];

export const CalendarTaskEditView = React.forwardRef<ICalendarTaskEditViewRefProps, ICalendarTaskEditViewProps>((props, ref) => {
    const orgStateHash = useRef("");
    const dataContainerDiv = useRef<HTMLDivElement>(null);
    const scrollToClassName = useRef("");
    const isNew = props.calendarTaskId === Base.emptyGuid;

    const getRepetitionIntervalWeek = (repetitionIntervalType: CalendarTaskRepetitionType, repetitionIntervalSub: number, repetitionStartDate: number, eventStartDate: number): number[] => {
        if (repetitionIntervalType !== CalendarTaskRepetitionType.Weekly) return [((eventStartDate ? (new Date(eventStartDate))?.getDay() : (new Date(repetitionStartDate))?.getDay()) + 6) % 7];
        const result: number[] = [];
        const str = Base.padZeros(repetitionIntervalSub.toString(10), 7);
        for (let i = 0; i < str.length; i++) {
            if (str[i] === "0") continue;
            result.push(i);
        }
        return result.length ? result : [0];
    };

    const getRepetitionIntervaMonth = (repetitionIntervalType: CalendarTaskRepetitionType, repetitionIntervalSub: number): number => {
        if (repetitionIntervalType !== CalendarTaskRepetitionType.Monthly) return (new Date()).getDate();
        return repetitionIntervalSub > 0 && repetitionIntervalSub < 32 ? repetitionIntervalSub : 1;
    };

    const getRepetitionIntervalYear = (repetitionIntervalType: CalendarTaskRepetitionType, repetitionIntervalSub: number): IRepetitionIntervalYear => {
        const nowDate = new Date();
        const result: IRepetitionIntervalYear = { month: nowDate.getMonth() + 1, day: nowDate.getDate() };
        if (repetitionIntervalType !== CalendarTaskRepetitionType.Yearly) return result;
        const str = Base.padZeros(repetitionIntervalSub.toString(10), 4);
        const parts = Base.getValidDateParts(str.substring(2).toInteger(), str.substring(0, 2).toInteger(), null);
        result.day = parts.day;
        result.month = parts.month;
        return result;
    };

    const getRepetitionIntervalSub = (saveState: ICalendarTaskEditViewState): number => {
        if (saveState.repetitionIntervalType === CalendarTaskRepetitionType.Weekly) {
            let value = 0;
            for (let i = 0; i < saveState.repetitionIntervalWeek.length; i++) {
                value = value + Math.pow(10, 6 - saveState.repetitionIntervalWeek[i]);
            }
            return value;
        } else if (saveState.repetitionIntervalType === CalendarTaskRepetitionType.Monthly) {
            return saveState.repetitionIntervalMonth;
        } else if (saveState.repetitionIntervalType === CalendarTaskRepetitionType.Yearly) {
            return saveState.repetitionIntervalYear.month * 100 + saveState.repetitionIntervalYear.day;
        }
        return 0;
    };
    
    const getSaveDataFromState = async (saveState: ICalendarTaskEditViewState): Promise<SaveDataObj> => {
        const data = new SaveDataObj();
        //General
        data.append("rowId", saveState.rowId);
        data.append("id", saveState.id);
        data.append("categoryId", saveState.categoryId ? saveState.categoryId : null);
        data.append("recordId", saveState.calendarTask.recordId);
        data.append("databaseRecordType", saveState.calendarTask.databaseRecordType);
        data.append("title", saveState.title);
        data.append("locationText", saveState.locationText);
        data.append("description", saveState.description);
        data.append("startDate", (Base.getDateTime(saveState.startDateStr, saveState.startTimeStr) ?? new Date()).getTime());
        data.append("endDate", Base.getDateTime(saveState.endDateStr, saveState.endTimeStr)?.getTime());
        data.append("allDay", saveState.allDay);
        data.append("isPrivate", saveState.isPrivate);
        data.append("state", saveState.state);
        data.append("remainder", saveState.remainder ? saveState.remainder : -1);
        data.append("repetitionIntervalType", saveState.repetitionIntervalType);
        data.append("repetitionInterval", saveState.repetitionInterval);
        data.append("repetitionIntervalSub", getRepetitionIntervalSub(saveState));
        data.append("repetitionEndRepeatCount", saveState.repetitionEndDateStr ? null : saveState.repetitionEndRepeatCount);
        data.append("repetitionEndDate", saveState.repetitionEndRepeatCount ? null : saveState.repetitionEndDateStr.toUtcDate().getTime());
        const attachments: SaveAttachmentBase64[] = [];
        if (isNew) {
            for (const fileAttachment of saveState.attachments.filter(i => !!i.file)) {
                const attachment = await SaveAttachmentBase64.fromFileAttachment(saveState.photoAttachmentIds.indexOf(fileAttachment.id) > -1 ? "photo" : "", fileAttachment);
                if (!attachment) continue;
                attachments.push(attachment);
            }
        }
        data.append("attachments", isNew ? attachments : null);
        data.append("addHandlers", isNew
            ? saveState.handlers.map(i => { return { code: i.code, type: i.date }; })
            : null
        );
        return data;
    };

    const getStateChangeFromProps = (props: ICalendarTaskEditViewProps, cameraIds: string[]): ICalendarTaskEditViewState => {
        let item = props.calendarTask;
        if (!item) {
            item = new CalendarTaskEditItem();
        }
        const result = {
            calendarTask: item,
            categories: props.categories ?? [],
            id: item.id ?? "",
            lockedUser: item.lockedUser,
            cameraIds,
            showCamera: false,
            showingCamera: false,
            showSelectReminderDialog: false,
            showSelectStateDialog: false,
            showSelectCategoryDialog: false,
            eventHandlersVisible: false,
            repetitionSettingsVisible: false,
            parentId: item.parentId,
            categoryId: item.categoryId,
            rowId: item.rowId,
            title: item.title,
            locationText: item.locationText,
            orgStartDate: item.startDate,
            startDateStr: Base.utcTimeToDateStr(item.startDate),
            startTimeStr: Base.timeToTimeStr(item.startDate),
            endDateStr: Base.utcTimeToDateStr(item.endDate),
            endTimeStr: item.endDate
                ? Base.timeToTimeStr(item.endDate)
                : "",
            repetitionIntervalType: item.repetitionIntervalType,
            repetitionInterval: item.repetitionInterval,
            repetitionIntervalWeek: getRepetitionIntervalWeek(item.repetitionIntervalType, item.repetitionIntervalSub, item.startDate, item.startDate),
            repetitionIntervalMonth: getRepetitionIntervaMonth(item.repetitionIntervalType, item.repetitionIntervalSub),
            repetitionIntervalYear: getRepetitionIntervalYear(item.repetitionIntervalType, item.repetitionIntervalSub),
            repetitionEndRepeatCount: item.repetitionEndRepeatCount,
            repetitionEndDateStr: item.repetitionEndDate
                ? Base.utcTimeToDateStr(item.repetitionEndDate)
                : "",
            state: item.state,
            allDay: item.allDay,
            isPrivate: item.isPrivate,
            remainder: item.remainder,
            description: item.description,
            attachments: item.attachments,
            handlers: item.handlers,
            photoAttachmentIds: [],
            removedAttachmentIds: [],
        };
        if (result.id) {
            // SET orgStateHash
            getSaveDataFromState(result)
                .then(saveData => {
                    orgStateHash.current = saveData.getHash(hashIgnoreSaveDataFields);
                });
        } else {
            orgStateHash.current = "";
        }
        return result;
    };

    // const [state, setState] = useState<ICalendarTaskEditViewState>(() => getStateFromProps(props, []));
    const [state, setState] = useState<ICalendarTaskEditViewState>({
        calendarTask: props.calendarTask,
        categories: props.categories ?? [],
        id: "",
        lockedUser: null,
        cameraIds: [],
        showCamera: false,
        showingCamera: false,
        showSelectReminderDialog: false,
        showSelectStateDialog: false,
        showSelectCategoryDialog: false,
        eventHandlersVisible: false,
        repetitionSettingsVisible: false,
        parentId: "",
        categoryId: "",
        rowId: "",
        title: "",
        locationText: "",
        orgStartDate: 0,
        startDateStr: "",
        startTimeStr: "",
        endDateStr: "",
        endTimeStr: "",
        repetitionIntervalType: CalendarTaskRepetitionType.NotRepeated,
        repetitionInterval: 0,
        repetitionIntervalWeek: [],
        repetitionIntervalMonth: 0,
        repetitionIntervalYear: { day: 0, month: 0 },
        repetitionEndRepeatCount: 0,
        repetitionEndDateStr: "",
        state: CalendarTaskState.NotStarted,
        allDay: false,
        isPrivate: false,
        remainder: -1,
        description: "",
        attachments: [],
        handlers: [],
        photoAttachmentIds: [],
        removedAttachmentIds: []
    });

    useEffect(() => {
        const setCameras = async () => {
            let cameraIds: string[] = [];
            try {
                cameraIds = await Base.getCameraIds();
            } catch (error) {
                cameraIds = [];
            }
            setState((old) => ({
                ...old,
                cameraIds
            }));
        };

        setCameras();
    }, []);

    useEffect(() => {
        if (!props.calendarTask?.id) return;
        setState((old) => ({
            ...old,
            ...getStateChangeFromProps(props, state.cameraIds.slice(0))
        }));
    }, [props.calendarTask?.id ?? ""]);

    //#region Phase 2: Attachments
    const handleAttachmentDownload = async (id: string) => {
        if (!id) return;
        const attachment = state.attachments.find(i => i.id === id);
        if (!attachment) return;
        if (!Base.isNullOrUndefined(attachment.file)) {
            downloadjs(attachment.file, attachment.name); //, "image/jpeg"
            return;
        }
        if (attachment.isNew()) return;
        const result = await AppUtils.callService(() => attachmentService.getAttachment(id), false);
        if (!result) return;
        AppUtils.showSuccessMessage(String.format(Translations.AttachmentParameterDownloaded, result));
    };

    const hasCameras = (): boolean => {
        return !!state.cameraIds && state.cameraIds.length > 0;
    };

    const handleAttachmentAddPhoto = () => {
        if (!hasCameras()) return;
        setState((old) => ({
            ...old,
            showCamera: true
        }));
    };

    const handleCameraStreamAvailable = () => {
        setState((old) => ({
            ...old,
            showingCamera: true
        }));
    };

    const setScrollToClassNameForCalendarTaskAttachments = () => {
        scrollToClassName.current = "calendarTaskAttachments";
    };

    const addAttachmentsAdd = async (attachments: FileAttachment[], isPhoto: boolean) => {
        if (attachments.length < 1) return;
        if (!isNew) {
            const saveData = await getAddAttachmentsSaveData(attachments, isPhoto);
            const result = await AppUtils.callService(() => calendarTaskService.calendarTaskEditAddAttachments(saveData.obj));
            if (!result) return;
            EditItem.setItemIdsFromIds(attachments, result.ids);
            for (const attachment of attachments) {
                attachment.rowId = "DUMMY";
            }
        }
        let stateChange = {};
        if (isPhoto) {
            stateChange = {
                photoAttachmentIds: state.photoAttachmentIds.concat(attachments.map(i => i.id))
            };
        }
        setState((old) => ({
            ...old,
            ...stateChange,
            attachments: state.attachments.concat(attachments),
        }));
    };

    const handleCameraPhotoOk = async (dataUrl: string) => {
        setScrollToClassNameForCalendarTaskAttachments();
        if (!dataUrl) {
            setState((old) => ({
                ...old,
                showCamera: false,
                showingCamera: false
            }));
            return;
        }
        const attachment = await FileAttachment.photoToFileAttachment(dataUrl, 0);
        await addAttachmentsAdd([attachment], true);
    };

    const handleCameraPhotoCancel = (permissionDenied: boolean) => {
        setScrollToClassNameForCalendarTaskAttachments();
        setState((old) => ({
            ...old,
            showCamera: false,
            showingCamera: false,
            cameraIds: permissionDenied
                ? []
                : state.cameraIds
        }));
    };

    const getRemoveAttachmentsSaveData = (attachmentIds: string[]): SaveDataObj => {
        const result = new SaveDataObj();
        result.append("id", state.id);
        result.append("ids", attachmentIds);
        return result;
    };

    const handleAttachmentRemove = async (id: string) => {
        if (!id) return;
        if (!isNew) {
            const saveData = getRemoveAttachmentsSaveData([id]);
            if (await AppUtils.showConfirmationDialog(String.format(Translations.DoYouReallyWantToRemoveAttachmentParameter, state.attachments.find(x => x.id === id)?.name), false) === ConfirmationDialogResult.No) return;
            const result = AppUtils.callService(() => calendarTaskService.calendarTaskEditRemoveAttachments(saveData.obj));
            if (!result) return;
        }
        setState((old) => ({
            ...old,
            attachments: state.attachments.filter(i => i.id !== id),
            photoAttachmentIds: state.photoAttachmentIds.filter(i => i !== id)
        }));
    };

    const getAddAttachmentsSaveData = async (attachments: IFileAttachment[], isPhoto: boolean): Promise<SaveDataObj> => {
        const result = new SaveDataObj();
        result.append("id", state.id);
        result.append("attachments", await SaveAttachmentBase64.fromFileAttachments(attachments, isPhoto ? "photo" : ""));
        return result;
    };

    const handleAttachmentsAdd = async (fileList: FileList) => {
        const attachments = await FileAttachment.addFileListToFileAttachments([], fileList, 0);
        await addAttachmentsAdd(attachments, false);
    };
    //#endregion

    //#region Locking
    const isLockedToUser = (): boolean => {
        return state.lockedUser?.code === props.user?.userCode;
    };

    const isLockedToOtherUser = (): boolean => {
        return state.lockedUser?.code && !(props.user?.userCode ?? "").equalIgnoreCase(state.lockedUser.code);
    };

    const unlockCalendarTask = async (id: string, lockToCurrentUser: boolean, afterUnlock: (result: IUnlockResult) => void) => {
        const result = await AppUtils.callService(() => calendarTaskService.unlock(id, lockToCurrentUser));
        if (!result) return null;
        // NO NEED TO UPDATE LIST VIEW, BECAUSE LIST VIEW items do not contain locked state
        // store.customStore.dispatch(storeActions.eventListSetLocked(id, false));
        if (afterUnlock) {
            afterUnlock(result);
        }
    };

    const closeView = (unlock: boolean): void => {
        if (unlock !== null || unlock !== undefined) {
            if (unlock) {
                unlockCalendarTask(state.calendarTask.id, false, () => {
                    props.onClose();
                });
            } else {
                props.onClose();
            }
        }
    };

    const handleUnlockClick = async () => {
        const item = state.calendarTask;
        if (!item) return;
        const result = await AppUtils.showConfirmationDialog(String.format(Translations.DoYouReallyWantToUnlockCalendarTask, item.title));
        if (result !== ConfirmationDialogResult.Yes) return;
        unlockCalendarTask(item.id, true, (result: IUnlockResult) => {
            setState((old) => ({
                ...old,
                lockedUser: StoreUtils.getNowModifyUser(result.lockedDate)
            }));
        });
    };
    //#endregion Locking

    const saveCalendarTask = async (): Promise<boolean> => {
        const isLockedToOther = isLockedToOtherUser();
        if (isLockedToOther) {
            AppUtils.showErrorMessage(String.format(Translations.LockedCalendarTaskParameterCannotBeSaved, state.lockedUser.name));
            return false;
        }
        const unlockOnClose = isLockedToUser() && !isNew;
        const saveData = await getSaveDataFromState(state);
        const result = await AppUtils.callService(() => calendarTaskService.saveCalendarTaskEdit(saveData.obj));
        //Call server
        if (!result) return false;
        closeView(unlockOnClose);
        return true;
    };

    const validateEventBasicInformation = (): boolean => {
        if (!state.title) {
            AppUtils.showErrorMessage(Translations.TitleMustBeDefined);
            return false;
        }
        if (!state.startDateStr) {
            AppUtils.showErrorMessage(Translations.CalendarTaskStartDateMustBeDefined);
            return false;
        }
        if (state.endDateStr) {
            const startDateTime = Base.getDateTime(state.startDateStr, state.startTimeStr);
            const endDateTime = Base.getDateTime(state.endDateStr, state.endTimeStr);
            if (startDateTime && endDateTime && startDateTime.getTime() > endDateTime.getTime()) {
                AppUtils.showErrorMessage(Translations.CalendarTaskEndDateMustBeAfterStartDate);
                return false;
            }
        }
        return true;
    };
        
    const handleSaveClick = async () => {
        if (!validateEventBasicInformation()) return;
        const result = await AppUtils.showConfirmationDialog(Translations.SaveCalendarTaskConfirmation, false, storeTypes.ConfirmationDialogType.Information, Translations.SaveCalendarTaskConfirmation);
        if (result === ConfirmationDialogResult.Yes) {
            saveCalendarTask();
        }
    };

    const handleBackClick = async () => {
        const unlockOnClose = !isLockedToOtherUser() && isLockedToUser() && !isNew;
        const cancelResult = await AppUtils.cancel((await getSaveDataFromState(state)).getHash(hashIgnoreSaveDataFields), orgStateHash.current);
        if (cancelResult === ConfirmationDialogResult.Cancel) return false;
        if (cancelResult === ConfirmationDialogResult.Yes) {
            saveCalendarTask();
        } else {
            closeView(unlockOnClose);
        }
        return true;
    };

    const handleChange = (event) => {
        const target = event.target;
        const value: string = target.value;
        const name: string = target.name;
        if (name === "title") {
            setState((old) => ({
                ...old,
                title: value
            }));
        } else if (name === "location") {
            setState((old) => ({
                ...old,
                locationText: value
            }));
        } else if (name === "description") {
            setState((old) => ({
                ...old,
                description: value
            }));
        }
    };

    const handleStartDateChange = (startDateStr: string) => {
        setState((old) => ({
            ...old,
            startDateStr
        }));
    };

    const handleEndDateChange = (value: string) => {
        let endDateStr = value;
        if (value && state.startDateStr) {
            const startDate = state.startDateStr.toUtcDate().getTime();
            const endDate = value.toUtcDate().getTime();
            if (endDate < startDate) {
                endDateStr = Base.utcTimeToDateStr(startDate);
            }
        }
        setState((old) => ({
            ...old,
            endDateStr
        }));
    };

    const handleStartTimeChange = (value: moment.Moment | string) => {
        if (typeof value === "string") {
            if (!value) {
                setState((old) => ({
                    ...old,
                    startTimeStr: ""
                }));
            }
        } else {
            setState((old) => ({
                ...old,
                startTimeStr: Base.timeToTimeStr(value)
            }));
        }
    };

    const handleStartTimeBlur = (value: moment.Moment | string) => {
        if (typeof value === "string") {
            setState((old) => ({
                ...old,
                startTimeStr: Base.timeToTimeStr(Base.roundTimeMinutes(value.toTime().getTime(), 15))
            }));
        } else {
            setState((old) => ({
                ...old,
                startTimeStr: Base.timeToTimeStr(Base.roundTimeMinutes(Base.timeToTimeStr(value).toTime().getTime(), 15))
            }));
        }
    };

    const handleEndTimeChange = (value: moment.Moment | string) => {
        if (typeof value === "string") {
            if (!value) {
                setState((old) => ({
                    ...old,
                    endTimeStr: ""
                }));
            }
        } else {
            setState((old) => ({
                ...old,
                endTimeStr: Base.timeToTimeStr(value)
            }));
        }
    };

    const handleEndTimeBlur = (value: moment.Moment | string) => {
        let endTimeStr: string;
        if (typeof value === "string") {
            endTimeStr = Base.timeToTimeStr(Base.roundTimeMinutes(value.toTime().getTime(), 15));
        } else {
            endTimeStr = Base.timeToTimeStr(Base.roundTimeMinutes(Base.timeToTimeStr(value).toTime().getTime(), 15));
        }
        let endDateStr = state.endDateStr;
        if (endTimeStr) {
            if (!endDateStr) {
                endDateStr = state.startDateStr;
            }
            const startDateTime = Base.getDateTime(state.startDateStr, state.startTimeStr);
            const endDateTime = Base.getDateTime(endDateStr, endTimeStr);
            if (startDateTime && endDateTime && startDateTime.getTime() > endDateTime.getTime()) {
                endDateStr = state.startDateStr;
                endTimeStr = state.startTimeStr;
            }
        }
        setState((old) => ({
            ...old,
            endDateStr,
            endTimeStr
        }));
    };

    const handleChangeReminderClick = () => {
        setState((old) => ({
            ...old,
            showSelectReminderDialog: true
        }));
    };

    const handleChangeReminderDialogOk = (id: string, _code: string) => {
        setState((old) => ({
            ...old,
            showSelectReminderDialog: false,
            remainder: parseInt(id, 10)
        }));
    };

    const handleChangeReminderDialogCancel = () => {
        setState((old) => ({
            ...old,
            showSelectReminderDialog: false
        }));
    };

    const handleLoadReminderTypes = (page: number): Promise<IEndlessList<IIdCode>> => {
        return new Promise<IEndlessList<IIdCode>>((resolve, reject) => {
            const result = new EndlessList<IIdCode>(null, IdCode);
            result.items = remainderIdCodes;
            result.page = 1;
            result.hasMore = false;
            resolve(result);
        });
    };

    const handleChangeStateClick = () => {
        setState((old) => ({
            ...old,
            showSelectStateDialog: true,
        }));
    };

    const handleChangeStateDialogOk = (id: string, _code: string) => {
        setState((old) => ({
            ...old,
            showSelectStateDialog: false,
            state: parseInt(id, 10)
        }));
    };

    const handleChangeStateDialogCancel = () => {
        setState((old) => ({
            ...old,
            showSelectStateDialog: false
        }));
    };

    const handleLoadStateTypes = (page: number): Promise<IEndlessList<IIdCode>> => {
        return new Promise<IEndlessList<IIdCode>>((resolve, reject) => {
            const result = new EndlessList<IIdCode>(null, IdCode);
            result.items = stateIdCodes;
            result.page = 1;
            result.hasMore = false;
            resolve(result);
        });
    };

    const handleChangeCategoryClick = () => {
        setState((old) => ({
            ...old,
            showSelectCategoryDialog: true
        }));
    };

    const handleChangeCategoryDialogOk = (id: string) => {
        setState((old) => ({
            ...old,
            showSelectCategoryDialog: false,
            categoryId: id
        }));
    };

    const handleChangeCategoryDialogCancel = () => {
        setState((old) => ({
            ...old,
            showSelectCategoryDialog: false
        }));
    };

    const handleRemoveEventHandler = async (userGroupCode: string): Promise<ApiSuccess> => {
        if (!userGroupCode) return null;
        if (!isNew) {
            const removeHandlers = state.handlers.filter(i => i.code === userGroupCode);
            const result = await AppUtils.callService(() => calendarTaskService.calendarTaskEditRemoveHandlers(state.id, removeHandlers.map(i => ({ code: i.code, type: i.date }))));
            if (!result) return null;
        }
        setState((old) => ({
            ...old,
            handlers: state.handlers.filter(i => i.code !== userGroupCode)
        }));
        return new ApiSuccess();
    };

    const handleAddEventHandlers = async (userGroups: IModifyUser[]): Promise<ApiSuccess> => {
        if (!userGroups || userGroups.length < 1) return null;
        if (!isNew) {
            const result = await AppUtils.callService(() => calendarTaskService.calendarTaskEditAddHandlers(state.id, userGroups.map(i => ({ code: i.code, type: i.date }))));
            if (!result) return null;
        }
        setState((old) => ({
            ...old,
            handlers: state.handlers.concat(userGroups)
        }));
        return new ApiSuccess();
    };

    const handleRepetitionSettingsChange = (name: string, value: string | number | IRepetitionIntervalYear | boolean | number[]) => {
        if (name === "repetitionIntervalType") {
            setState((old) => ({
                ...old,
                repetitionIntervalType: value as number
            }));
        } else if (name === "repetitionEndRepeatCount") {
            setState((old) => ({
                ...old,
                repetitionEndRepeatCount: value as number
            }));
        } else if (name === "repetitionEndDateStr") {
            setState((old) => ({
                ...old,
                repetitionEndDateStr: value as string
            }));
        } else if (name === "repetitionInterval") {
            setState((old) => ({
                ...old,
                repetitionInterval: value as number
            }));
        } else if (name === "repetitionIntervalYear") {
            setState((old) => ({
                ...old,
                repetitionIntervalYear: value as IRepetitionIntervalYear
            }));
        } else if (name === "repetitionIntervalMonth") {
            setState((old) => ({
                ...old,
                repetitionIntervalMonth: value as number
            }));
        } else if (name === "repetitionIntervalWeek") {
            setState((old) => ({
                ...old,
                repetitionIntervalWeek: value as number[]
            }));
        } else if (name === "repetitionSettingsVisible") {
            setState((old) => ({
                ...old,
                repetitionSettingsVisible: value as boolean
            }));
        }
    };

    useImperativeHandle(ref, () => {
        return {
            getOrgStateHash() {
                return orgStateHash.current;
            },
            getLockedUser() {
                return state.lockedUser;
            },
            getSaveDataHash() {
                return getSaveDataFromState(state).then(saveData => saveData.getHash(hashIgnoreSaveDataFields));
            },
            save() {
                return saveCalendarTask();
            },
            close() {
                return handleBackClick();
            },
        };
    }, [state]);

    if (!state.calendarTask) {
        return (
            <div className={props.inFullView ? "pageBackground" : ""} style={ { zIndex: 2 } }>
                {props.inFullView &&
                    <EditPageHeader
                        title={Translations.Calendar}
                        onBackClick={handleBackClick}
                    />
                }
            </div>
        );
    }

    const showCamera = hasCameras() && state.showCamera;
    const showingCamera = showCamera && state.showingCamera;
    const isLockedToOther = isLockedToOtherUser();
    const isReadOnly = props.isReadOnly || isLockedToOther || !props.canSaveCalendarTask;
    const selectedCategory = state.categories.find(item => item.id === state.categoryId);
    const handlersSectionIsEditable = true;
    const calendarTask = state.calendarTask;
    const isRelatedToRecord = !Base.isNullOrUndefined(calendarTask.databaseRecordType);

    return (
        <div className={props.inFullView ? "pageBackground" : ""} style={ { zIndex: 2 } }>
            {!showingCamera && props.inFullView &&
                <>
                    <EditPageHeader
                        title={(calendarTask.parentId
                            ? Translations.CalendarTaskOccurrence
                            : (calendarTask.hasChildren
                                ? Translations.CalendarTaskSeries
                                : Translations.CalendarTask)) + " - " + (isNew ? Translations.ANew : calendarTask.title)}
                        onBackClick={handleBackClick}
                    />
                    <Footer
                        isReadOnly={isReadOnly}
                        onClick={handleSaveClick}
                    />
                </>
            }
            {!showingCamera &&
                <div className={(props.inFullView ? "pageContentContainer " : "") + "mainEdit calendarTaskEdit"}>
                    <div className="dataContainer" ref={dataContainerDiv} >
                        <div className="sectionTitle">{Translations.CalendarTask}</div>
                        <LockedToOtherUser
                            isLockedToOtherUser={isLockedToOther}
                            userIsIsSystemAdmin={props.userIsIsSystemAdmin}
                            lockedUserPicture={state.lockedUser?.picture}
                            lockedUserName={state.lockedUser?.name}
                            onUnlockClick={handleUnlockClick}
                        />
                        <div>
                            <FormRow
                                label={Translations.Title}
                                required={true}
                            >
                                <input
                                    className="form-control"
                                    type="text"
                                    name="title"
                                    title={Translations.Title}
                                    maxLength={50}
                                    readOnly={isReadOnly}
                                    disabled={isReadOnly}
                                    value={state.title}
                                    required={true}
                                    onChange={handleChange}
                                />
                            </FormRow>
                            <FormRow
                                label={Translations.Location}
                            >
                                <input className="form-control"
                                    type="text"
                                    name="location"
                                    title={Translations.Location}
                                    maxLength={50}
                                    readOnly={isRelatedToRecord || isReadOnly}
                                    disabled={isRelatedToRecord || isReadOnly}
                                    value={state.locationText}
                                    onChange={handleChange}
                                />
                            </FormRow>
                            <div className="row" style={{ marginTop: "8px" }}>
                                <div className="col-6">
                                    <FormGroup>
                                        <CheckBox
                                            classes={"large"}
                                            checked={state.allDay}
                                            title={Translations.AllDay}
                                            disabled={isReadOnly}
                                            onClickBoolean={(checked: boolean) => {
                                                let startTimeStr = state.startTimeStr;
                                                let endTimeStr = state.endTimeStr;
                                                if (checked) {
                                                    startTimeStr = "0:00";
                                                    endTimeStr = "0:00";
                                                }
                                                setState((old) => ({
                                                    ...old,
                                                    allDay: checked,
                                                    startTimeStr,
                                                    endTimeStr
                                                }));
                                            }}
                                        />
                                    </FormGroup>
                                </div>
                                {!isRelatedToRecord &&
                                    <div className="col-6">
                                        <FormGroup>
                                            <CheckBox
                                                classes={"large"}
                                                checked={state.isPrivate}
                                                title={Translations.Private}
                                                disabled={isReadOnly || !props.canShareCalendarTasks}
                                                onClickBoolean={(checked: boolean) => {
                                                    setState((old) => ({
                                                        ...old,
                                                        isPrivate: checked
                                                    }));
                                                }}
                                            />
                                        </FormGroup>
                                    </div>
                                }
                            </div>
                            <FormRow
                                label={Translations.Starts}
                                required={true}
                            >
                                <div className="row">
                                    <div className="col-6">
                                        <DatePicker
                                            required={true}
                                            disabled={isReadOnly}
                                            value={state.startDateStr}
                                            onChange={handleStartDateChange}
                                            onBlur={handleStartDateChange}
                                        />
                                    </div>
                                    <div className="col-6">
                                        {(!state.allDay
                                            ? <Datetime
                                                locale={appConfig.culture}
                                                className={"roWhite right"}
                                                value={state.startTimeStr}
                                                dateFormat={false}
                                                timeFormat={"H:mm"}
                                                timeConstraints={ { minutes: { step: 15, min: 0, max: 59 } } }
                                                input={true}
                                                closeOnSelect={true}
                                                inputProps={{ maxLength: 10, readOnly: true, disabled: isReadOnly }}
                                                onChange={handleStartTimeChange}
                                                onBlur={handleStartTimeBlur}
                                            />
                                            : <input
                                                className="form-control disabled"
                                                readOnly={true}
                                                disabled={true}
                                                value={state.startTimeStr}
                                            />
                                        )}
                                    </div>
                                </div>
                            </FormRow>
                            <FormRow
                                label={Translations.Ends}
                            >
                                <div className="row">
                                    <div className="col-6">
                                        <DatePicker
                                            required={true}
                                            disabled={isReadOnly}
                                            value={state.endDateStr}
                                            minDate={state.startDateStr}
                                            onChange={handleEndDateChange}
                                            onBlur={handleEndDateChange}
                                        />
                                    </div>
                                    <div className="col-6">
                                        {(!state.allDay
                                            ? <Datetime
                                                locale={appConfig.culture}
                                                className={"roWhite right"}
                                                value={state.endTimeStr}
                                                dateFormat={false}
                                                timeFormat={"H:mm"}
                                                timeConstraints={{ minutes: { step: 15, min: 0, max: 59 } }}
                                                input={true}
                                                closeOnSelect={true}
                                                inputProps={{ maxLength: 10, readOnly: true, disabled: isReadOnly }}
                                                onChange={handleEndTimeChange}
                                                onBlur={handleEndTimeBlur}
                                                onFocus={() => {
                                                    //Initialize time to same as start time if end date is same as start date or undefined
                                                    if (!state.endTimeStr && !!state.startTimeStr && (!state.endDateStr || state.startDateStr === state.endDateStr)) {
                                                        setState((old) => ({
                                                            ...old,
                                                            endTimeStr: state.startTimeStr
                                                        }));
                                                    }
                                                }}
                                            />
                                            : <input
                                                className="form-control disabled"
                                                readOnly={true}
                                                disabled={true}
                                                value={state.endTimeStr}
                                            />
                                        )}
                                    </div>
                                </div>
                            </FormRow>
                            <div className="form-group">
                                <div>
                                    <label className="control-label">{Translations.Reminder}</label>
                                    <ToolButton
                                        tooltip={Translations.Change}
                                        disabled={isReadOnly}
                                        classes={"edit round mono smaller right"}
                                        onClick={handleChangeReminderClick}
                                    />
                                </div>
                                <div>
                                    <span>{remainderIdCodes.find((reminder) => reminder.id === state.remainder.toString(10))?.code}</span>
                                </div>
                            </div>
                            <FormRow
                                label={Translations.Description}
                            >
                                <textarea
                                    className="form-control medium"
                                    name="description"
                                    title={Translations.Description}
                                    readOnly={isReadOnly}
                                    disabled={isReadOnly}
                                    value={state.description}
                                    onChange={handleChange}
                                />
                            </FormRow>
                            <div className="form-group">
                                <div>
                                    <label className="control-label">{Translations.State}</label>
                                    <ToolButton
                                        tooltip={Translations.Change}
                                        disabled={isReadOnly}
                                        classes={"edit round mono smaller right"}
                                        onClick={handleChangeStateClick}
                                    />
                                </div>
                                <div>
                                    <span>{stateIdCodes.find(item => parseInt(item.id) === state.state)?.code}</span>
                                </div>
                            </div>
                            <div className="form-group">
                                <div>
                                    <label className="control-label">{Translations.Category}</label>
                                    <ToolButton
                                        tooltip={Translations.Change}
                                        disabled={isReadOnly}
                                        classes={"edit round mono smaller right"}
                                        onClick={handleChangeCategoryClick}
                                    />
                                </div>
                                <div>
                                    <CalendarTaskCategoryText
                                        item={
                                            IdColorTitle.createIdColorTitle(
                                                selectedCategory?.id ?? "",
                                                selectedCategory?.color ?? "",
                                                selectedCategory?.code ?? `'${Translations.SelectCategory}'`
                                            )
                                        }
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="row calendarTaskAttachments">
                            <div className="col-12">
                                <AttachmentList
                                    isReadOnly={isReadOnly}
                                    canShowCamera={hasCameras()}
                                    attachments={state.attachments}
                                    onDownloadAttachment={handleAttachmentDownload}
                                    onRemoveAttachment={handleAttachmentRemove}
                                    onAddAttachments={handleAttachmentsAdd}
                                    onAddPhoto={handleAttachmentAddPhoto}
                                />
                            </div>
                        </div>
                        {!state.isPrivate && !isRelatedToRecord &&
                            <>
                                <div className="sectionTitle">
                                    {Translations.Visibility + " (" + (state.handlers.length ? state.handlers.length.toString(10) : "*") + ")"}
                                    <div
                                        className={"btn " + (state.eventHandlersVisible ? "collapseUp" : "expandDown")}
                                        onClick={() => {
                                            setState((old) => ({
                                                ...old,
                                                eventHandlersVisible: !state.eventHandlersVisible
                                            }));
                                        }}
                                    >
                                        <span className="icon"></span>
                                    </div>
                                </div>
                                {state.eventHandlersVisible && handlersSectionIsEditable &&
                                    <div>
                                        <div className="row">
                                            <div className="col-12">
                                                <CalendarTaskHandlerList
                                                    isReadOnly={isReadOnly}
                                                    handlers={state.handlers}
                                                    noHandlersMessage={Translations.AllCalendarTaskUsers}
                                                    onRemoveHandler={handleRemoveEventHandler}
                                                    onAddHandlers={handleAddEventHandlers}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                }
                            </>
                        }
                        {!state.parentId &&
                            <CalendarTaskRepetitionSettings
                                repetitionSettingsVisible={state.repetitionSettingsVisible}
                                orgStartDate={state.orgStartDate}
                                startDateStr={state.startDateStr}
                                isReadOnly={isReadOnly}
                                repetitionIntervalType={state.repetitionIntervalType}
                                repetitionInterval={state.repetitionInterval}
                                repetitionIntervalWeek={state.repetitionIntervalWeek}
                                repetitionIntervalMonth={state.repetitionIntervalMonth}
                                repetitionIntervalYear={state.repetitionIntervalYear}
                                repetitionEndRepeatCount={state.repetitionEndRepeatCount}
                                repetitionEndDateStr={state.repetitionEndDateStr}
                                onChange={handleRepetitionSettingsChange}
                            />
                        }
                    </div>
                </div>
            }
            {!showingCamera && props.inFullView &&
                <div className="toolbar">
                    {!isReadOnly &&
                        <div className="buttons">
                            <ToolButton
                                classes="save mui"
                                title={Translations.Save}
                                tooltip={Translations.Save}
                                onClick={handleSaveClick}
                            />
                        </div>
                    }
                </div>
            }
            {showCamera &&
                <CameraPhoto
                    cameraIds={state.cameraIds}
                    cameraStreamAvailable={state.showingCamera}
                    onCameraStreamAvailable={handleCameraStreamAvailable}
                    onOkClick={handleCameraPhotoOk}
                    onCancelClick={handleCameraPhotoCancel}
                />
            }
            {state.showSelectReminderDialog &&
                <SelectIdCodeDialog
                    title={Translations.SelectReminder}
                    selectedId={state.remainder.toString(10)}
                    loadIdCodes={handleLoadReminderTypes}
                    onOk={handleChangeReminderDialogOk}
                    onCancel={handleChangeReminderDialogCancel}
                />
            }
            {state.showSelectStateDialog &&
                <SelectIdCodeDialog
                    title={Translations.SelectState}
                    selectedId={state.state.toString(10)}
                    loadIdCodes={handleLoadStateTypes}
                    onOk={handleChangeStateDialogOk}
                    onCancel={handleChangeStateDialogCancel}
                />
            }
            {state.showSelectCategoryDialog &&
                <SelectCalendarTaskCategory
                    categories={(isRelatedToRecord
                        ? []
                        : [IdColorTitle.createIdColorTitle("", null, `'${Translations.SelectCategory}'`)]).concat(
                        state.categories.map(
                            (item) => IdColorTitle.createIdColorTitle(item.id, item.color, item.code)
                        )
                    )}
                    categoryId={state.categoryId}
                    onOk={handleChangeCategoryDialogOk}
                    onCancel={handleChangeCategoryDialogCancel}
                />
            }
        </div>
    );
});

CalendarTaskEditView.displayName = "CalendarTaskEditView";
